import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Calendar, Plus, Trash2, AlertCircle, Edit2 } from 'lucide-react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ShopClosuresManager = () => {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClosure, setEditingClosure] = useState(null);
  const [newClosure, setNewClosure] = useState({
    closureDate: '',
    reason: '',
    isRecurring: false,
  });

  useEffect(() => {
    loadClosures();
  }, []);

  const loadClosures = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/shop-closures`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClosures(response.data);
    } catch (error) {
      console.error('Load closures error:', error);
      toast.error('Failed to load shop closures');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClosure = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_URL}/shop-closures`,
        newClosure,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Shop closure added successfully!');
      setShowAddModal(false);
      setNewClosure({ closureDate: '', reason: '', isRecurring: false });
      loadClosures();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add closure');
    }
  };

  const handleOpenEditModal = (closure) => {
    setEditingClosure({
      id: closure.id,
      closureDate: closure.closure_date,
      reason: closure.reason || '',
      isRecurring: closure.is_recurring,
    });
    setShowEditModal(true);
  };

  const handleEditClosure = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/shop-closures/${editingClosure.id}`,
        {
          reason: editingClosure.reason,
          isRecurring: editingClosure.isRecurring,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Closure date updated successfully!');
      setShowEditModal(false);
      setEditingClosure(null);
      loadClosures();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update closure');
    }
  };

  const handleDeleteClosure = async (id) => {
    if (!window.confirm('Are you sure you want to remove this closure date?')) return;
    
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_URL}/shop-closures/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Closure date removed');
      loadClosures();
    } catch (error) {
      toast.error('Failed to remove closure');
    }
  };

  const groupClosuresByYear = () => {
    const grouped = {};
    closures.forEach(closure => {
      const year = new Date(closure.closure_date).getFullYear();
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(closure);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const groupedClosures = groupClosuresByYear();
  const years = Object.keys(groupedClosures).sort((a, b) => b - a);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Calendar className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">Shop Closure Dates</h2>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Add Closure Date</span>
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Set specific dates when the shop is closed (holidays, special events, etc.). 
        No appointments can be booked on these dates.
      </p>

      {/* Alert for recurring closures */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
        <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-blue-800">
          <strong>Recurring Closures:</strong> Mark holidays as recurring to automatically apply them every year 
          (e.g., Christmas, New Year's Day). You only need to add them once!
        </div>
      </div>

      {/* Closures List */}
      {years.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
          <p>No closure dates set. Add your first closure date above.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {years.map(year => (
            <div key={year}>
              <h3 className="text-lg font-bold mb-3 text-gray-700">{year}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {groupedClosures[year]
                  .sort((a, b) => new Date(a.closure_date) - new Date(b.closure_date))
                  .map(closure => (
                    <div
                      key={closure.id}
                      className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="font-bold text-gray-900">
                            {format(parseISO(closure.closure_date), 'EEEE, MMMM d, yyyy')}
                          </div>
                          {closure.is_recurring && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                              🔄 Recurring Annually
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleOpenEditModal(closure)}
                            className="text-blue-600 hover:text-blue-700 p-1"
                            title="Edit closure"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteClosure(closure.id)}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="Delete closure"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      {closure.reason && (
                        <p className="text-sm text-gray-600 mt-2">{closure.reason}</p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Closure Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-6">Add Shop Closure Date</h2>

            <form onSubmit={handleAddClosure} className="space-y-4">
              <div>
                <label className="label">Closure Date *</label>
                <input
                  type="date"
                  required
                  value={newClosure.closureDate}
                  onChange={(e) => setNewClosure({ ...newClosure, closureDate: e.target.value })}
                  className="input"
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div>
                <label className="label">Reason</label>
                <input
                  type="text"
                  value={newClosure.reason}
                  onChange={(e) => setNewClosure({ ...newClosure, reason: e.target.value })}
                  className="input"
                  placeholder="e.g., Christmas Day, Staff Training, etc."
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={newClosure.isRecurring}
                  onChange={(e) => setNewClosure({ ...newClosure, isRecurring: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="isRecurring" className="text-sm font-medium text-gray-700">
                  Recurring annually (e.g., holidays)
                </label>
              </div>

              {newClosure.isRecurring && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    This closure will automatically apply to the same date every year. 
                    For example, if you set December 25th, the shop will be closed every December 25th.
                  </p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewClosure({ closureDate: '', reason: '', isRecurring: false });
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn btn-primary">
                  Add Closure
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Closure Modal */}
      {showEditModal && editingClosure && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-6">Edit Shop Closure</h2>

            <form onSubmit={handleEditClosure} className="space-y-4">
              <div>
                <label className="label">Closure Date</label>
                <input
                  type="date"
                  value={editingClosure.closureDate}
                  disabled
                  className="input bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Date cannot be changed. Delete and create a new closure to change the date.
                </p>
              </div>

              <div>
                <label className="label">Reason</label>
                <input
                  type="text"
                  value={editingClosure.reason}
                  onChange={(e) => setEditingClosure({ ...editingClosure, reason: e.target.value })}
                  className="input"
                  placeholder="e.g., Christmas Day, Staff Training, etc."
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="editIsRecurring"
                  checked={editingClosure.isRecurring}
                  onChange={(e) => setEditingClosure({ ...editingClosure, isRecurring: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="editIsRecurring" className="text-sm font-medium text-gray-700">
                  Recurring annually (e.g., holidays)
                </label>
              </div>

              {editingClosure.isRecurring && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    This closure will automatically apply to the same date every year. 
                    For example, if this is December 25th, the shop will be closed every December 25th.
                  </p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingClosure(null);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopClosuresManager;
